import { useState } from 'react'
import { Modal } from './Modal'
import { CheckboxGroup, type CheckboxOption } from './primitives/CheckboxGroup'
import { Notice } from './primitives/Notice'
import { delta, isEntitlementAllowed, optionsFor, wouldEmptyTheSet } from '../api/entitlements'
import { principalClassLabel } from '../theme/vocabulary'
import type { PrincipalClass } from '../api/types'

// Entitlement editor (ATC-131 / ATC-074).
//
// Submits a DELTA, never a whole-list replacement — a replacement makes
// accidental removal a one-typo operation on the set of services a live
// credential may call.
export function EntitlementEditModal({
  customerEnvironmentId,
  principalClass,
  before,
  busy,
  error,
  onConfirm,
  onCancel,
}: {
  customerEnvironmentId: string
  principalClass: PrincipalClass
  before: string[]
  busy: boolean
  error: string | null
  onConfirm: (add: string[], remove: string[]) => void
  onCancel: () => void
}) {
  const [checked, setChecked] = useState<string[]>([...before])
  const [confirmingRemoval, setConfirmingRemoval] = useState(false)

  const options: CheckboxOption[] = optionsFor(before).map((option) => {
    if (!isEntitlementAllowed(option.value, principalClass)) {
      // DISABLED WITH A REASON, not hidden. Hiding `ppkg` on a device
      // credential makes ATC-ADR-016's rule invisible; a disabled box with the
      // reason beside it teaches it. The type makes the hint mandatory here.
      return {
        value: option.value,
        label: option.value,
        disabled: true,
        hint: 'Flight Deck credentials only — a build endpoint reachable with a token that lives on every device would be a package factory.',
      }
    }
    if (!option.known) {
      return {
        value: option.value,
        label: option.value,
        // Rendered and TICKED, because a value absent from this set is one the
        // delta computes as removed. See `optionsFor`.
        hint: 'Not recognised by this console — left as-is. Unticking it will remove it.',
      }
    }
    return { value: option.value, label: option.value }
  })

  const change = delta(before, checked)
  const empty = wouldEmptyTheSet(checked)
  const removing = change.remove.length > 0

  function submit() {
    if (removing && !confirmingRemoval) {
      setConfirmingRemoval(true)
      return
    }
    onConfirm(change.add, change.remove)
  }

  return (
    <Modal title="Edit entitlements" onClose={onCancel}>
      <p>
        <strong>{principalClassLabel(principalClass)}</strong> credential on{' '}
        <code className="cei">{customerEnvironmentId}</code>.
      </p>

      <CheckboxGroup
        legend="Entitled services"
        options={options}
        value={checked}
        onChange={(next) => {
          setChecked(next)
          setConfirmingRemoval(false)
        }}
        disabled={busy}
      />

      {empty && (
        <Notice tone="warn">
          A credential cannot be left entitled to nothing. That is not a revocation — set the
          status to revoked instead, which is named, audited and reversible.
        </Notice>
      )}

      {/* A ONE-STEP `.btn-danger` CONFIRM WITH AN IMPACT LIST, deliberately NOT
          the amber-trigger → red-confirm escalation. That pattern was promoted
          into suite Tier 1 for an IRREVERSIBLE re-key; an entitlement removal is
          reversible in ten seconds by ticking the box again. Diluting the
          escalation across every serious-but-reversible action is how it stops
          meaning anything. */}
      {confirmingRemoval && (
        <Notice tone="error">
          Removing {change.remove.join(', ')} takes effect within ~60 seconds: that service starts
          returning 403 for this credential. Re-adding it is a second edit, not an undo.
        </Notice>
      )}

      {error && <Notice tone="error">{error}</Notice>}

      {change.empty && !error && <p className="muted">Nothing has changed yet.</p>}

      <div className="modal-actions">
        <button onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className={removing ? 'btn-danger' : 'btn-primary'}
          disabled={busy || change.empty || empty}
          onClick={submit}
        >
          {busy
            ? 'Saving…'
            : confirmingRemoval
              ? `Remove ${change.remove.join(', ')}`
              : 'Save changes'}
        </button>
      </div>
    </Modal>
  )
}
